import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  PlayCircle,
  Star,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import api from '@/lib/api';
import { buildImageUrl, formatMoney, normalizeLocalizedData, repairArabicMojibake } from '@/lib/utils';
import { useLang } from '@/contexts/LangContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { trackEvent } from '@/lib/tracking';

function pickText(lang, arText, enText) {
  return lang === 'ar' ? repairArabicMojibake(arText) : enText;
}

function normalizeDisplayValue(value, fallback = '-') {
  if (value === null || value === undefined) return fallback;
  const text = String(repairArabicMojibake(value)).trim();
  return text || fallback;
}

function getPackageTier(card) {
  const tokens = [
    card?.style_variant,
    card?.name,
    card?.card_category_value,
    card?.card_sessions_value,
    card?.option_one_value,
    card?.option_two_value,
    card?.option_three_value,
    card?.option_four_value,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (/mini[\s_-]*group/.test(tokens)) return 'mini_group';
  if (/\bv(?:\s*\.?\s*)?i(?:\s*\.?\s*)?p\b|private|1\s*(?:to|-)?\s*1|one[\s-]*to[\s-]*one/.test(tokens)) return 'vip';
  if (/\bgroup\b/.test(tokens)) return 'group';
  return 'other';
}

function getPackageDiscountConfig(card) {
  const payload = card?.discount || {};
  const oldPrice = Number(payload.old_price || 0);
  const newPrice = Number(payload.price ?? payload.new_price ?? 0);
  const currency = String(payload.currency || card?.currency || 'AED');
  const badgeLabel = String(payload.badge_label || 'Discount');
  const directEnabled = Boolean(payload.enabled) && oldPrice > 0 && newPrice > 0 && newPrice < oldPrice;
  if (directEnabled) {
    return { enabled: true, oldPrice, newPrice, currency, badgeLabel };
  }
  return { enabled: false, oldPrice: 0, newPrice: 0, currency, badgeLabel };
}

function getEffectiveCardPrice(card, discountConfig) {
  if (discountConfig?.enabled) return discountConfig.newPrice;
  if (card?.effective_price !== undefined && card?.effective_price !== null) return Number(card.effective_price || 0);
  return Number(card?.price || 0);
}

function getEffectiveCardCurrency(card, discountConfig) {
  if (discountConfig?.enabled) return discountConfig.currency;
  return card?.currency || discountConfig?.currency || 'AED';
}

export default function CourseDetailPage() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);
  const [relatedCourses, setRelatedCourses] = useState([]);
  const [trustBar, setTrustBar] = useState([]);
  const [whyUs, setWhyUs] = useState([]);
  const [steps, setSteps] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [socialPosts, setSocialPosts] = useState([]);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedPricingRules, setSelectedPricingRules] = useState({});
  const [trialOpen, setTrialOpen] = useState(false);
  const [requestMode, setRequestMode] = useState('trial');
  const [trialLoading, setTrialLoading] = useState(false);
  const [trialForm, setTrialForm] = useState({ full_name: '', email: '', phone: '', country_code: '+971', notes: '' });

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      api.get(`/courses/${slug}`).catch(() => ({ data: null })),
      api.get('/content/trust-bar').catch(() => ({ data: [] })),
      api.get('/content/why-choose-us').catch(() => ({ data: [] })),
      api.get('/content/steps').catch(() => ({ data: [] })),
      api.get('/content/testimonials').catch(() => ({ data: [] })),
      api.get('/social-posts').catch(() => ({ data: [] })),
    ]).then(([courseRes, trustRes, whyRes, stepsRes, testimonialsRes, socialRes]) => {
      if (!active) return;
      setCourse(normalizeLocalizedData(courseRes.data || null));
      setTrustBar(normalizeLocalizedData(Array.isArray(trustRes.data) ? trustRes.data : []));
      setWhyUs(normalizeLocalizedData(Array.isArray(whyRes.data) ? whyRes.data : []));
      setSteps(normalizeLocalizedData(Array.isArray(stepsRes.data) ? stepsRes.data : []));
      setTestimonials(normalizeLocalizedData(Array.isArray(testimonialsRes.data) ? testimonialsRes.data : []));
      setSocialPosts(normalizeLocalizedData(Array.isArray(socialRes.data) ? socialRes.data : []));
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [slug, lang]);

  useEffect(() => {
    if (!course) return;
    api.get('/courses', {
      params: {
        language_id: course.language_id || undefined,
        audience: course.audience || 'individual',
      },
    })
      .then((res) => {
        const data = normalizeLocalizedData(Array.isArray(res.data) ? res.data : []);
        setRelatedCourses(data.filter((item) => item.slug !== course.slug).slice(0, 3));
      })
      .catch(() => setRelatedCourses([]));
  }, [course, lang]);

  useEffect(() => {
    setTrialForm((current) => ({
      ...current,
      full_name: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : current.full_name,
      email: user?.email || current.email,
      phone: user?.phone || current.phone,
    }));
  }, [user]);

  const galleryImages = useMemo(() => {
    if (!course) return [];
    if (Array.isArray(course.gallery_images) && course.gallery_images.length > 0) {
      return [{ id: 'hero-main', title: course.title, image_url: course.image_url }, ...course.gallery_images.filter((item) => item.image_url)];
    }
    return [{ id: 'hero-main', title: course.title, image_url: course.image_url }];
  }, [course]);

  const activeImage = galleryImages[activeImageIndex] || galleryImages[0];
  const packageCards = course?.package_cards?.cards || [];
  const pricingCards = useMemo(() => {
    const pricing = course?.pricing;
    if (!pricing?.enabled || !Array.isArray(pricing.rules) || pricing.rules.length === 0) {
      return [];
    }

    const groups = pricing.rules.reduce((acc, rule) => {
      const rawName = String(rule.name || '').trim();
      const key = rawName || `rule-${rule.id}`;
      if (!acc[key]) {
        acc[key] = {
          id: `pricing-${key}`,
          name: rawName || pickText(lang, 'باقة', 'Package'),
          variants: [],
        };
      }
      acc[key].variants.push(rule);
      return acc;
    }, {});

    return Object.values(groups);
  }, [course, lang]);

  const pricingSummaryLabels = useMemo(() => ({
    category: course?.pricing?.summary_labels?.category || pickText(lang, 'الفئة', 'Category'),
    sessions: course?.pricing?.summary_labels?.sessions || pickText(lang, 'الجلسات', 'Sessions'),
    duration: course?.pricing?.summary_labels?.duration || pickText(lang, 'المدة', 'Duration'),
    fee: course?.pricing?.summary_labels?.fee || pickText(lang, 'مجاني', 'Free'),
  }), [course, lang]);

  const pricingOptionOneMeta = useMemo(() => ({
    label: course?.pricing?.labels?.option_one || pickText(lang, 'الفئة', 'Category'),
    placeholder: course?.pricing?.placeholders?.option_one || pickText(lang, 'اختر خياراً', 'Select an option'),
  }), [course, lang]);

  const pricingDisplayCards = useMemo(() => {
    if (pricingCards.length === 0) return [];
    return pricingCards.map((card) => {
      const selectedRuleId = String(selectedPricingRules[card.id] || '');
      const selectedRule = card.variants.find((rule) => String(rule.id) === selectedRuleId) || card.variants[0];
      return {
        id: card.id,
        type: 'pricing',
        name: card.name,
        variants: card.variants,
        selected_rule_id: String(selectedRule?.id || ''),
        option_one_value: selectedRule?.option_one || '',
        option_two_value: selectedRule?.option_two || '',
        option_three_value: selectedRule?.option_three || '',
        option_four_value: selectedRule?.option_four || '',
        price: selectedRule?.price || 0,
        effective_price: selectedRule?.effective_price ?? selectedRule?.price ?? 0,
        discount: selectedRule?.discount || null,
        currency: selectedRule?.currency || 'AED',
        enroll_label: course?.pricing?.actions?.enroll || t('courses.enroll_now'),
        trial_label: course?.pricing?.actions?.trial || pickText(lang, 'حصة تجريبية', 'Trial Class'),
        card_category_value: selectedRule?.summary?.category || selectedRule?.option_one || '',
        card_sessions_value: selectedRule?.summary?.sessions || selectedRule?.option_two || '',
        card_duration_value: selectedRule?.summary?.duration || selectedRule?.option_three || '',
        card_free_value: selectedRule?.summary?.fee || selectedRule?.option_four || '',
        summary: '',
      };
    });
  }, [course, lang, pricingCards, selectedPricingRules, t]);

  const displayCards = pricingDisplayCards.length > 0 ? pricingDisplayCards : packageCards;
  const packageCardsMeta = useMemo(() => {
    return {
      title: course?.package_cards?.title || course?.title,
      description: course?.package_cards?.description || pickText(lang, 'اختر الباقة المناسبة لهدفك وجدولك الدراسي.', 'Review the available packages and choose the one that fits your schedule and goals.'),
    };
  }, [course, lang]);
  const packageCardsMetaResolved = pricingDisplayCards.length > 0
    ? {
        title: course?.pricing?.title || course?.title,
        description: course?.pricing?.description || pickText(lang, 'اختر الفئة المناسبة وشاهد السعر والتفاصيل تتحدث مباشرة.', 'Choose the category that fits you and see the price and details update instantly.'),
      }
    : packageCardsMeta;
  const backToCoursesHref = `/courses${location.search || ''}`;
  const courseTitleLength = Array.from(course?.title || '').length;
  const courseTitleDensity = courseTitleLength > 58 ? 'is-extra-long' : courseTitleLength > 34 ? 'is-long' : '';
  const courseTitleViewportSize = courseTitleLength > 0
    ? Math.min(6.1, Math.max(2.05, 150 / courseTitleLength))
    : 5.6;

  useEffect(() => {
    if (pricingCards.length === 0) {
      setSelectedPricingRules({});
      return;
    }

    setSelectedPricingRules((current) => {
      const next = {};
      pricingCards.forEach((card) => {
        const currentValue = current[card.id];
        const hasCurrent = card.variants.some((rule) => String(rule.id) === String(currentValue));
        next[card.id] = hasCurrent ? currentValue : String(card.variants[0]?.id || '');
      });
      return next;
    });
  }, [pricingCards]);

  useEffect(() => {
    setSelectedPackage((current) => {
      if (!displayCards.length) return null;
      if (!current?.id) return displayCards[0];
      return displayCards.find((item) => item.id === current.id) || displayCards[0];
    });
  }, [displayCards]);

  const handleEnroll = (packageCard = null) => {
    setSelectedPackage(packageCard || selectedPackage);
    setRequestMode('enroll');
    setTrialOpen(true);
  };

  const handleViewPackages = () => {
    const target = document.getElementById('packages-cards-section');
    if (!target) return;

    const navbarOffset = 110;
    const targetPosition = target.getBoundingClientRect().top + window.scrollY - navbarOffset;

    window.scrollTo({
      top: targetPosition,
      behavior: 'smooth',
    });
  };

  const handleTrialSubmit = async (event) => {
    event.preventDefault();
    if (!course?.id) return;
    if (!trialForm.full_name || !trialForm.email) {
      toast.error(pickText(lang, 'الاسم الكامل والبريد الإلكتروني مطلوبان.', 'Full name and email are required.'));
      return;
    }

    const requestPrefix = requestMode === 'enroll'
      ? pickText(lang, 'طلب تسجيل', 'Enrollment request')
      : pickText(lang, 'طلب حصة تجريبية', 'Trial class request');
    const submittedNotes = trialForm.notes?.trim()
      ? `[${requestPrefix}] ${trialForm.notes.trim()}`
      : `[${requestPrefix}]`;

    setTrialLoading(true);
    try {
      const selectedDiscount = getPackageDiscountConfig(selectedPackage);
      await api.post(`/courses/${course.id}/trial-request`, {
        ...trialForm,
        notes: submittedNotes,
        request_type: requestMode,
        option_one_value: selectedPackage?.name || '',
        option_two_value: selectedPackage?.card_category_value || '',
        option_three_value: selectedPackage?.card_duration_value || '',
        option_four_value: selectedPackage?.card_free_value || '',
        quoted_price: getEffectiveCardPrice(selectedPackage, selectedDiscount),
      });
      const eventName = requestMode === 'trial' ? 'trial_request_submit_success' : 'course_request_submit_success';
      trackEvent(eventName, {
        form_id: 'course_request_modal',
        form_type: 'course_request',
        source_page: location.pathname,
        source_component: 'CourseDetailPage',
        route: '/courses/:slug',
        language: lang,
        course_id: course.id,
        course_slug: course.slug,
        request_type: requestMode,
        status: 'success',
      });
      setTrialOpen(false);
      navigate('/thank-you/inquiry-received', {
        state: {
          form_type: 'course_request',
          source_page: location.pathname,
          source_component: 'CourseDetailPage',
          request_type: requestMode,
          verified_submission: true,
        },
      });
    } catch (error) {
      toast.error(error.response?.data?.error || pickText(lang, 'تعذر إرسال الطلب حالياً.', 'We could not send the request right now.'));
    } finally {
      setTrialLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#f5f6f8] px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl space-y-8">
          <Skeleton className="h-[420px] rounded-[2rem]" />
          <Skeleton className="h-[520px] rounded-[2rem]" />
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="bf-course-detail-page bg-[#f5f6f8] px-4 py-24 text-center sm:px-6 lg:px-8">
        <p className="bf-brand-kicker text-[#6c8bc6]">{pickText(lang, 'الدورات', 'Courses')}</p>
        <h1 className="bf-page-display mt-4">{pickText(lang, 'لم يتم العثور على الدورة', 'Course not found')}</h1>
        <div className="mt-8"><Link to={backToCoursesHref}><Button className="bf-premium-cta bf-ui-button rounded-full bg-[#6c8bc6] px-6 text-white">{pickText(lang, 'العودة إلى الدورات', 'Back to courses')}</Button></Link></div>
      </div>
    );
  }

  return (
    <div className="bf-page-canvas bf-course-detail-page bg-[#f5f6f8] text-[#231f20] dark:bg-[#231f20] dark:text-[#e6e6e6]" data-testid="course-detail-page">
      <section className="bf-course-cinema-hero relative isolate overflow-hidden px-4 pb-12 pt-28 sm:px-6 lg:px-8 lg:pb-16 lg:pt-32">
        <img src={buildImageUrl(activeImage?.image_url)} alt={course.title} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(28,35,51,0.12),rgba(28,35,51,0.44)_42%,rgba(28,35,51,0.76))]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_8%,rgba(108,139,198,0.32),transparent_34%),radial-gradient(circle_at_82%_22%,rgba(244,245,247,0.16),transparent_26%)]" />
        <div className="relative mx-auto flex min-h-[650px] max-w-7xl flex-col justify-between py-5 sm:py-8 lg:py-10">
              <div className="bf-course-hero-nav flex flex-wrap items-center gap-3 text-white/88">
                <Link to={backToCoursesHref} className="bf-course-hero-link inline-flex items-center gap-2 rounded-full border border-white/18 bg-white/10 px-4 py-2 text-sm font-medium backdrop-blur hover:bg-white/16">
                  <ArrowLeft className="h-4 w-4" />
                  {pickText(lang, 'العودة إلى الدورات', 'Back to courses')}
                </Link>
              </div>

              <div className="bf-course-hero-copy max-w-4xl text-white">
                  <p className="bf-brand-kicker text-[#c3d0e8]">{course.language_name || pickText(lang, 'الدورات', 'Courses')}</p>
                  <h1
                    className={`bf-course-hero-title mt-4 ${courseTitleDensity}`.trim()}
                    style={{ '--course-title-vw': `${courseTitleViewportSize}vw` }}
                    title={course.title}
                  >
                    {course.title}
                  </h1>
                  <p className="bf-page-copy mt-4 max-w-2xl text-white/82 sm:text-lg">{course.short_description}</p>
                  <div className="bf-course-hero-actions mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                    <Button
                      type="button"
                      className="bf-premium-cta campaign-packages-cta h-12 rounded-full bg-[#6f93d6] px-8 text-[15px] font-semibold text-white shadow-[0_18px_42px_rgba(111,147,214,0.35)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#82a5e6] focus:outline-none focus:ring-2 focus:ring-[#9bb8ef]/60"
                      onClick={handleViewPackages}
                    >
                      <span className="relative z-10">{pickText(lang, 'شاهد الباقات', 'View Packages')}</span>
                    </Button>
                    <span className="bf-course-hero-note text-sm leading-7 text-white/68">
                      {pickText(lang, 'انتقل مباشرة إلى خيارات التسجيل والأسعار.', 'Jump straight to package options and enrollment.')}
                    </span>
                  </div>
              </div>

              {trustBar.length > 0 && (
                <div className="bf-course-trust-orbit mt-8">
                  <div className="bf-hero-trust-shell" aria-label={pickText(lang, 'جهات الاعتماد والشركاء', 'Accreditations and partners')}>
                    <div className="bf-hero-trust-label">
                      <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                      <span>{pickText(lang, 'موثوق ومعتمد', 'Trusted & Accredited')}</span>
                    </div>
                    <div className="bf-hero-trust-track">
                      <span className="bf-hero-trust-signal" aria-hidden="true" />
                    {trustBar.slice(0, 6).map((item, index) => (
                      <a
                        key={item.name}
                        href={item.link || '#'}
                        className="bf-hero-trust-badge"
                        aria-label={item.name}
                        title={item.name}
                        style={{ '--trust-delay': `${index * 85}ms` }}
                      >
                        <span className="bf-hero-trust-logo-wrap">
                          {item.logo_url ? (
                            <img src={buildImageUrl(item.logo_url)} alt={item.name} className="bf-hero-trust-logo" />
                          ) : (
                            <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                          )}
                          </span>
                      </a>
                    ))}
                    </div>
                  </div>
                </div>
              )}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8 lg:py-16">
        <div className="space-y-10">
          <div>
            <div className="max-w-3xl">
              <p className="bf-brand-kicker text-[#6c8bc6]">{pickText(lang, 'تفاصيل الدورة', 'Course details')}</p>
              <h2 className="bf-subsection-display mt-3">{course.title}</h2>
              {course.description ? (
                <div className="prose prose-slate mt-6 max-w-none prose-headings:text-[#231f20] prose-a:text-[#6c8bc6] dark:prose-invert" dangerouslySetInnerHTML={{ __html: course.description }} />
              ) : (
                <p className="mt-6 text-base leading-8 text-[#231f20]/70 dark:text-[#e6e6e6]/72">{course.short_description}</p>
              )}
            </div>
          </div>

          <div>
          {displayCards.length > 0 ? (
            <div className="space-y-7">
              <Card className="bf-luxury-card rounded-[2rem] border-0 bg-white dark:bg-white/5">
                <CardContent className="p-6 sm:p-7">
                  <p className="bf-ui-label text-[#6c8bc6]">{pickText(lang, 'التسعير والتسجيل', 'Pricing & enrollment')}</p>
                  <h3 className="bf-subsection-display mt-3">{packageCardsMetaResolved.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-[#231f20]/70 dark:text-[#cfd7e6]">
                    {packageCardsMetaResolved.description}
                  </p>
                </CardContent>
              </Card>
              <div id="packages-cards-section" className="grid scroll-mt-[110px] gap-5 md:grid-cols-2 xl:grid-cols-3">
              {displayCards.map((item) => {
                const packageTier = getPackageTier(item);
                const cardDiscountConfig = getPackageDiscountConfig(item);
                const showDiscount = Boolean(cardDiscountConfig.enabled);
                const showFreeLabel = packageTier === 'group' || packageTier === 'mini_group' || packageTier === 'vip';
                const effectivePrice = getEffectiveCardPrice(item, cardDiscountConfig);
                const effectiveCurrency = getEffectiveCardCurrency(item, cardDiscountConfig);
                return (
                <Card
                  key={item.id || item.name}
                  onClick={() => setSelectedPackage(item)}
                  className={`bf-package-card h-full cursor-pointer rounded-[2rem] border-0 transition-colors ${
                    selectedPackage?.id === item.id
                      ? 'is-selected bg-white dark:bg-white/5'
                      : 'bg-white dark:bg-white/5'
                  }`}
                >
                  <CardContent className="bf-package-card-inner flex h-full flex-col p-6 sm:p-7">
                    <div className="bf-package-card-header flex flex-wrap items-start justify-between gap-4">
                      <div>
                        {item.ribbon_text ? (
                          <p className="bf-ui-label text-[#6c8bc6]">{item.ribbon_text}</p>
                        ) : null}
                        <h3 className="bf-card-display mt-1 text-2xl">{item.name}</h3>
                      </div>
                      <div className="bf-package-badge-stack flex flex-col items-end gap-2">
                        <button
                          type="button"
                          onClick={(event) => { event.stopPropagation(); setSelectedPackage(item); }}
                          className={`bf-package-select-badge rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] ${
                            selectedPackage?.id === item.id
                              ? 'bg-[#6c8bc6] text-white'
                              : 'bg-[#edf1f8] text-[#6c8bc6] dark:bg-white/10'
                          }`}
                        >
                          {selectedPackage?.id === item.id ? pickText(lang, 'مختارة', 'Selected') : pickText(lang, 'الباقة', 'Package')}
                        </button>
                        {showDiscount ? (
                          <span className="rounded-full border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-emerald-700 dark:border-emerald-400/40 dark:bg-emerald-500/10 dark:text-emerald-300">
                            {pickText(lang, 'خصم', cardDiscountConfig.badgeLabel || 'Discount')}
                          </span>
                        ) : null}
                      </div>
                    </div>

                    {showDiscount ? (
                      <div className="bf-package-price-block mt-4 space-y-1">
                        <p className="bf-package-old-price text-sm font-medium text-[#6b7280] line-through dark:text-[#9ca3af]">
                          {formatMoney(cardDiscountConfig.oldPrice, cardDiscountConfig.currency)}
                        </p>
                        <p className="bf-package-new-price text-3xl font-semibold text-[#15803d] dark:text-[#4ade80]">
                          {formatMoney(effectivePrice, effectiveCurrency)}
                        </p>
                      </div>
                    ) : (
                      <p className="bf-package-new-price mt-4 text-3xl font-semibold">{formatMoney(effectivePrice, effectiveCurrency)}</p>
                    )}

                    {item.type === 'pricing' && item.variants.length > 1 ? (
                      <div className="mt-5">
                        <p className="mb-2 text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-[#9fb0cf]">
                          {pricingOptionOneMeta.label}
                        </p>
                        <Select
                          value={item.selected_rule_id}
                          onValueChange={(value) => {
                            setSelectedPricingRules((current) => ({ ...current, [item.id]: value }));
                          }}
                        >
                          <SelectTrigger className="rounded-2xl border border-[#d8dde6] bg-[#edf1f8] text-[#231f20] dark:border-white/12 dark:bg-[#161c27] dark:text-white">
                            <SelectValue placeholder={pricingOptionOneMeta.placeholder} />
                          </SelectTrigger>
                          <SelectContent className="border border-[#d8dde6] bg-white text-[#231f20] dark:border-white/12 dark:bg-[#161c27] dark:text-white">
                            {item.variants.map((variant) => (
                              <SelectItem key={variant.id} value={String(variant.id)}>
                                {normalizeDisplayValue(variant.option_one)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ) : null}

                    <div className="bf-package-metrics mt-5 grid grid-cols-2 gap-3">
                      <div className="bf-package-metric rounded-2xl border border-[#dce3ef] bg-[#edf1f8] p-3 dark:border-white/12 dark:bg-[#161c27]">
                        <p className="text-[0.68rem] uppercase tracking-[0.18em] text-slate-500 dark:text-[#9fb0cf]">{pricingSummaryLabels.category}</p>
                        <p className="mt-1 text-sm font-semibold text-[#231f20] dark:text-white">{normalizeDisplayValue(item.card_category_value)}</p>
                      </div>
                      <div className="bf-package-metric rounded-2xl border border-[#dce3ef] bg-[#edf1f8] p-3 dark:border-white/12 dark:bg-[#161c27]">
                        <p className="text-[0.68rem] uppercase tracking-[0.18em] text-slate-500 dark:text-[#9fb0cf]">{pricingSummaryLabels.sessions}</p>
                        <p className="mt-1 text-sm font-semibold text-[#231f20] dark:text-white">{normalizeDisplayValue(item.card_sessions_value)}</p>
                      </div>
                      <div className="bf-package-metric rounded-2xl border border-[#dce3ef] bg-[#edf1f8] p-3 dark:border-white/12 dark:bg-[#161c27]">
                        <p className="text-[0.68rem] uppercase tracking-[0.18em] text-slate-500 dark:text-[#9fb0cf]">{pricingSummaryLabels.duration}</p>
                        <p className="mt-1 text-sm font-semibold text-[#231f20] dark:text-white">{normalizeDisplayValue(item.card_duration_value)}</p>
                      </div>
                      <div className="bf-package-metric rounded-2xl border border-[#dce3ef] bg-[#edf1f8] p-3 dark:border-white/12 dark:bg-[#161c27]">
                        <p className="text-[0.68rem] uppercase tracking-[0.18em] text-slate-500 dark:text-[#9fb0cf]">
                          {showFreeLabel ? pickText(lang, 'مجاني', 'FREE') : pricingSummaryLabels.fee}
                        </p>
                        <p className="mt-1 text-sm font-semibold text-[#231f20] dark:text-white">{normalizeDisplayValue(item.card_free_value)}</p>
                      </div>
                    </div>

                    {item.summary ? (
                      <p className="mt-5 text-sm leading-7 text-[#231f20]/72 dark:text-[#cfd7e6]">{item.summary}</p>
                    ) : null}

                    <div className="bf-package-actions mt-auto pt-6">
                      <div className="flex flex-col gap-3">
                      <Button type="button" className="bf-premium-cta h-12 w-full rounded-full bg-[#6c8bc6] text-white hover:bg-[#5876b0]" onClick={(event) => { event.stopPropagation(); handleEnroll(item); }}>
                        {pickText(lang, 'استفسر', 'Inquire')} <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                      <Button type="button" variant="outline" className="bf-package-trial-button h-12 w-full rounded-full" onClick={(event) => { event.stopPropagation(); setSelectedPackage(item); setRequestMode('trial'); setTrialOpen(true); }}>
                        {item.trial_label || pickText(lang, 'حصة تجريبية', 'Trial Class')}
                      </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )})}
              </div>
            </div>
          ) : (
          <Card className="rounded-[2rem] border border-[#d8dde6] bg-white shadow-[0_24px_60px_-38px_rgba(15,23,42,0.28)] dark:border-white/10 dark:bg-white/5 dark:shadow-none">
            <CardContent className="p-6 sm:p-7">
              <p className="bf-ui-label text-[#6c8bc6]">{pickText(lang, 'التسعير والتسجيل', 'Pricing & enrollment')}</p>
              <h3 className="bf-subsection-display mt-3">{pickText(lang, 'لا توجد باقات مضافة بعد', 'No pricing packages configured yet')}</h3>
              <p className="mt-3 text-sm leading-7 text-[#231f20]/70 dark:text-[#cfd7e6]">
                {pickText(lang, 'يجب إضافة باقات التسعير لهذا الكورس من Odoo حتى تظهر كروت التسعير هنا.', 'Pricing packages need to be added for this course in Odoo before they can appear here.')}
              </p>
            </CardContent>
          </Card>
          )}
          </div>
        </div>
      </section>

      <section className="bg-white py-14 dark:bg-white/5 sm:py-16">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[0.95fr_1.05fr] lg:px-8">
          <div>
            <p className="bf-brand-kicker text-[#6c8bc6]">{pickText(lang, 'لماذا تختار هذه الدورة', 'Why choose this course')}</p>
            <h2 className="bf-subsection-display mt-3">{pickText(lang, `لماذا تناسبك ${course.title}؟`, `Why ${course.title} works`)}</h2>
            <p className="mt-4 text-base leading-8 text-[#231f20]/70 dark:text-[#cfd7e6]">{pickText(lang, 'يجمع هذا البرنامج بين الهيكل الواضح والتطبيق العملي وخيارات الدراسة المرنة.', 'The program combines clear structure, practical use, and flexible study options.')}</p>
            <div className="mt-8 space-y-4">
              {(course.features || whyUs.map((item) => item.title)).slice(0, 5).map((feature, index) => (
                <div key={`${feature}-${index}`} className="flex items-start gap-3">
                  <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#6c8bc6]/12 text-[#6c8bc6]">
                    <CheckCircle2 className="h-4 w-4" />
                  </div>
                  <p className="text-sm leading-7">{typeof feature === 'string' ? feature : feature.title}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="overflow-hidden rounded-[2rem] border border-[#d8dde6] bg-[#edf1f8] p-3 dark:border-white/10 dark:bg-white/5">
            <div className="relative min-h-[420px] overflow-hidden rounded-[1.6rem]">
              <img src={buildImageUrl(activeImage?.image_url || course.image_url)} alt={course.title} className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(35,31,32,0),rgba(35,31,32,0.38))]" />
            </div>
          </div>
        </div>
      </section>

      {steps.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8 lg:py-16">
          <div className="mx-auto max-w-2xl text-center">
            <p className="bf-brand-kicker text-[#6c8bc6]">{pickText(lang, 'ماذا يحدث بعد ذلك', 'What happens next')}</p>
            <h2 className="bf-subsection-display mt-3">{pickText(lang, 'بعد اختيار هذه الدورة', 'After you choose this course')}</h2>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-4">
            {steps.slice(0, 4).map((item, index) => (
              <motion.div
                key={`${item.step}-${index}`}
                initial={{ opacity: 0, y: 28, scale: 0.96 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ delay: index * 0.12, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              >
                <Card className="bf-luxury-card h-full rounded-[1.8rem] border-0 bg-white transition-transform duration-300 dark:bg-white/5">
                  <CardContent className="p-6">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#231f20] text-white dark:bg-[#6c8bc6]">{item.step}</div>
                    <h3 className="bf-card-display mt-5 text-lg">{item.title}</h3>
                    <p className="mt-3 text-sm leading-7 text-[#231f20]/70 dark:text-[#cfd7e6]">{item.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {relatedCourses.length > 0 && (
        <section className="bg-white px-4 py-14 dark:bg-white/5 sm:px-6 lg:px-8 lg:py-16">
          <div className="mx-auto max-w-7xl">
            <div className="mb-8 flex items-end justify-between gap-4">
              <div>
                <p className="bf-brand-kicker text-[#6c8bc6]">{pickText(lang, 'دورات ذات صلة', 'Related courses')}</p>
                <h2 className="bf-subsection-display mt-3">{pickText(lang, 'قد تعجبك أيضاً', 'You may also like')}</h2>
              </div>
              <Link to="/courses"><Button variant="outline" className="bf-outline-button rounded-full">{pickText(lang, 'كل الدورات', 'All courses')}</Button></Link>
            </div>
            <div className="grid gap-5 md:grid-cols-3">
              {relatedCourses.map((item, index) => (
                <motion.div key={item.id} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.06 }}>
                  <Link to={`/courses/${item.slug}`}>
                    <Card className="bf-course-card overflow-hidden rounded-[1.8rem] border-0 bg-white dark:bg-white/5">
                      <div className="bf-course-card-media h-48 overflow-hidden"><img src={buildImageUrl(item.image_url)} alt={item.title} className="h-full w-full object-cover" /></div>
                      <CardContent className="bf-course-card-body p-5">
                        <h3 className="bf-course-card-title text-xl font-semibold">{item.title}</h3>
                        <p className="mt-2 text-sm leading-7 text-[#231f20]/70 dark:text-[#e6e6e6]/72">{item.short_description}</p>
                        <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#6c8bc6]">{t('courses.details')} <ChevronRight className="h-4 w-4" /></div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {testimonials.length > 0 && (
        <section className="px-4 py-14 sm:px-6 lg:px-8 lg:py-16">
          <div className="mx-auto max-w-7xl">
            <p className="bf-brand-kicker text-center text-[#6c8bc6]">{pickText(lang, 'آراء الطلاب', 'Student reviews')}</p>
            <div className="mt-8 grid gap-5 md:grid-cols-3">
              {testimonials.slice(0, 3).map((item) => {
                const imageUrl = buildImageUrl(item.image_url);
                const videoUrl = buildImageUrl(item.video_url);
                const hasVideo = Boolean(videoUrl);

                return (
                  <Card key={item.name} className="bf-testimonial-card overflow-hidden rounded-[1.8rem] border-0 bg-white dark:bg-white/5">
                    <CardContent className="p-6">
                      {hasVideo ? (
                        <div className="bf-testimonial-media is-video relative mb-5 overflow-hidden rounded-[1.2rem] bg-[#231f20]/8 dark:bg-black/30">
                          <video controls preload="metadata" poster={imageUrl || undefined} src={videoUrl} className="h-full w-full object-cover" />
                          <div className="pointer-events-none absolute left-3 top-3 inline-flex items-center gap-2 rounded-full bg-black/50 px-3 py-1 text-xs font-semibold text-white">
                            <PlayCircle className="h-3.5 w-3.5" />
                            {pickText(lang, 'فيديو', 'Video')}
                          </div>
                        </div>
                      ) : imageUrl ? (
                        <div className="bf-testimonial-media mb-5 overflow-hidden rounded-[1.2rem]">
                          <img src={imageUrl} alt={item.name} className="h-full w-full object-cover" />
                        </div>
                      ) : null}
                      <div className="mb-4 flex gap-1">{Array.from({ length: item.rating || 5 }).map((_, starIndex) => <Star key={starIndex} className="h-4 w-4 fill-[#6c8bc6] text-[#6c8bc6]" />)}</div>
                      <div className="mt-6"><p className="font-semibold">{item.name}</p><p className="text-sm text-[#231f20]/60 dark:text-[#e6e6e6]/62">{item.country}</p></div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {socialPosts.length > 0 && (
        <section className="bg-white px-4 py-14 dark:bg-white/5 sm:px-6 lg:px-8 lg:py-16">
          <div className="mx-auto max-w-7xl">
            <p className="bf-brand-kicker text-center text-[#6c8bc6]">{pickText(lang, 'وسائل التواصل الاجتماعي', 'Social media')}</p>
            <div className="mt-8 grid gap-5 md:grid-cols-3">
              {socialPosts.slice(0, 3).map((post) => (
                <a key={post.id} href={post.post_url} target="_blank" rel="noopener noreferrer">
                  <Card className="bf-social-post-card overflow-hidden rounded-[1.8rem] border-0 bg-white dark:bg-white/5">
                    {post.image_url ? <div className="h-52 overflow-hidden"><img src={buildImageUrl(post.image_url)} alt={post.name} className="h-full w-full object-cover" /></div> : null}
                    <CardContent className="p-5">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#6c8bc6]">{post.platform}</p>
                      <h3 className="bf-card-display mt-3 text-lg">{post.name}</h3>
                    </CardContent>
                  </Card>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      <Dialog open={trialOpen} onOpenChange={setTrialOpen}>
        <DialogContent className="bf-dialog-surface max-w-lg rounded-[1.8rem]">
          <DialogHeader>
            <DialogTitle>{requestMode === 'enroll' ? pickText(lang, 'طلب تسجيل', 'Enrollment request') : pickText(lang, 'طلب حصة تجريبية', 'Trial class request')}</DialogTitle>
            <DialogDescription>{requestMode === 'enroll' ? pickText(lang, 'أرسل بياناتك وسيتواصل معك الفريق لإتمام التسجيل.', 'Send your details and the team will follow up to complete your enrollment.') : pickText(lang, 'أرسل بياناتك وسيتواصل معك الفريق لترتيب الحصة.', 'Send your details and the team will follow up to arrange the class.')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleTrialSubmit} className="space-y-4">
            <Input value={trialForm.full_name} onChange={(event) => setTrialForm((current) => ({ ...current, full_name: event.target.value }))} placeholder={pickText(lang, 'الاسم الكامل', 'Full name')} />
            <Input type="email" value={trialForm.email} onChange={(event) => setTrialForm((current) => ({ ...current, email: event.target.value }))} placeholder={pickText(lang, 'البريد الإلكتروني', 'Email')} />
            <Input value={trialForm.phone} onChange={(event) => setTrialForm((current) => ({ ...current, phone: event.target.value }))} placeholder={pickText(lang, 'رقم الهاتف', 'Phone')} />
            <Textarea value={trialForm.notes} onChange={(event) => setTrialForm((current) => ({ ...current, notes: event.target.value }))} placeholder={pickText(lang, 'ملاحظات إضافية', 'Additional notes')} />
            <div className="flex gap-3">
              <Button type="submit" className="bf-premium-cta flex-1 rounded-full bg-[#6c8bc6] text-white hover:bg-[#5876b0]" disabled={trialLoading}>{trialLoading ? pickText(lang, 'جارٍ الإرسال...', 'Sending...') : requestMode === 'enroll' ? pickText(lang, 'تسجيل', 'Enroll') : pickText(lang, 'إرسال الطلب', 'Send request')}</Button>
              <Button type="button" variant="outline" className="bf-outline-button rounded-full" onClick={() => setTrialOpen(false)}>{t('common.cancel')}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}


